import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { fromIsoDate, toIsoDate } from '@/lib/date-range';

/** Where the year select starts when no `min` is given. */
const EARLIEST_DROPDOWN_MONTH = new Date(1900, 0, 1);

/**
 * A single-date control: an outline button that opens the shadcn calendar in a
 * popover. The value is a `yyyy-MM-dd` string (the shape forms and API params
 * already carry), so it drops in where a native `<input type="date">` was.
 * Picking a day applies it and closes. `dropdowns` swaps the month title for
 * month and year selects, for dates far from today (a date of birth).
 */
export interface DatePickerProps {
  /** `yyyy-MM-dd`, or empty for no selection. */
  value: string;
  onChange: (next: string) => void;
  /** Earliest selectable day (`yyyy-MM-dd`); days before it are disabled. */
  min?: string;
  /** Latest selectable day (`yyyy-MM-dd`); days after it are disabled. */
  max?: string;
  /** Month and year selects in the header, spanning `min` (or 1900) to `max` (or today). */
  dropdowns?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  dropdowns = false,
  placeholder = 'Pick a date',
  className,
  id,
}: DatePickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const selected = fromIsoDate(value);
  const minDate = fromIsoDate(min);
  const maxDate = fromIsoDate(max);
  const disabled = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            'w-full justify-start gap-2 px-3 font-normal',
            !selected && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate">
            {selected ? format(selected, 'MMM d, yyyy') : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected ?? minDate ?? maxDate}
          disabled={disabled.length > 0 ? disabled : undefined}
          {...(dropdowns && {
            captionLayout: 'dropdown' as const,
            startMonth: minDate ?? EARLIEST_DROPDOWN_MONTH,
            endMonth: maxDate ?? new Date(),
          })}
          onSelect={(date) => {
            if (!date) return;
            onChange(toIsoDate(date));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
